import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

/**
 * The arithmetic behind the Seasonality calendar: how one month's budget is
 * spread over its days. It is the month graph's own model one level down —
 * every day is AUTO, LOCKED or PINNED, exactly as a month is — so the rules
 * here mirror PlanSeasonalityTab.vue's rebalance()/monthAmount() rather than
 * inventing new ones:
 *
 * - a PINNED day holds an exact euro amount, taken off the top of the month;
 * - every other day is a weight sharing what the pins leave;
 * - a LOCKED day keeps the weight it was given, and the AUTO days split what
 *   is left of 100 in proportion to their impressions.
 *
 * So days only ever redistribute their month: whatever is set here, the days
 * add up to the month's budget, and only the pins can ask for more than it has.
 */

/** Keyed by `YYYY-MM-DD`. */
export type DayMap<T> = Record<string, T>;

export type CalendarDay = {
	date: string;
	day: number;
	/** False for the days of the neighbouring months that complete a week. */
	inMonth: boolean;
};

export type CalendarWeek = {
	isoWeek: number;
	/** Seven days, Monday first. */
	days: CalendarDay[];
	/** Just the days that belong to this month — what the week is made of here. */
	inMonth: string[];
};

/** Every date of the month, `YYYY-MM-DD`. */
export const monthDates = (year: number, month: number): string[] => {
	const first = dayjs(new Date(year, month - 1, 1));

	return Array.from({ length: first.daysInMonth() }, (_, i) =>
		first.add(i, "day").format("YYYY-MM-DD")
	);
};

/**
 * The month as ISO weeks, Monday first. A week that runs into the month
 * before or after is cut at the month's edge: its other days are carried for
 * display only (`inMonth: false`) and belong to — and are edited in — their
 * own month.
 *
 * @param month 1-12
 */
export const calendarWeeks = (year: number, month: number): CalendarWeek[] => {
	const first = dayjs(new Date(year, month - 1, 1));
	const last = first.endOf("month").startOf("day");

	const weeks: CalendarWeek[] = [];
	let cursor = first.startOf("isoWeek");

	while (!cursor.isAfter(last)) {
		const days = Array.from({ length: 7 }, (_, i) => {
			const day = cursor.add(i, "day");

			return {
				date: day.format("YYYY-MM-DD"),
				day: day.date(),
				inMonth: day.month() === month - 1,
			};
		});

		weeks.push({
			isoWeek: cursor.isoWeek(),
			days,
			inMonth: days.filter((day) => day.inMonth).map((day) => day.date),
		});

		cursor = cursor.add(1, "week");
	}

	return weeks;
};

/**
 * Each day's share (0-100) of the month's impressions — the AUTO days'
 * weighting and the reference mark on every bar. With no impressions at all
 * the month is split evenly rather than divided by zero.
 */
export const impressionShares = (
	dates: string[],
	impressions: DayMap<number>
): DayMap<number> => {
	const total = dates.reduce((sum, date) => sum + (impressions[date] ?? 0), 0);

	return Object.fromEntries(
		dates.map((date) => [
			date,
			total > 0
				? ((impressions[date] ?? 0) / total) * 100
				: 100 / Math.max(1, dates.length),
		])
	);
};

/**
 * Spreads whatever weight the locked days leave across the AUTO ones, in
 * proportion to their impression share. Pinned days sit outside this
 * entirely, as pinned months do — their euros are settled separately, so
 * their weight is not competing for a share of the hundred.
 *
 * Returns a new map; locked and pinned days keep their weight untouched.
 */
export const rebalanceDays = (
	dates: string[],
	weights: DayMap<number>,
	locked: DayMap<boolean>,
	pins: DayMap<number | null>,
	shares: DayMap<number>
): DayMap<number> => {
	const isPinned = (date: string) => pins[date] !== undefined && pins[date] !== null;

	const lockedWeight = dates.reduce(
		(sum, date) =>
			sum + (locked[date] && !isPinned(date) ? (weights[date] ?? 0) : 0),
		0
	);
	const movable = dates.filter((date) => !locked[date] && !isPinned(date));

	const next = { ...weights };
	if (!movable.length) return next;

	const shareSum = movable.reduce((sum, date) => sum + (shares[date] ?? 0), 0);
	const rest = Math.max(0, 100 - lockedWeight);

	movable.forEach((date) => {
		next[date] =
			shareSum > 0
				? (rest * (shares[date] ?? 0)) / shareSum
				: rest / movable.length;
	});

	return next;
};

/** Euros each day gets: its pin, or its weight's share of what the pins leave. */
export const dayAmounts = (
	dates: string[],
	weights: DayMap<number>,
	pins: DayMap<number | null>,
	budget: number
): DayMap<number> => {
	const isPinned = (date: string) => pins[date] !== undefined && pins[date] !== null;

	const pinnedTotal = dates.reduce(
		(sum, date) => sum + (isPinned(date) ? (pins[date] as number) : 0),
		0
	);
	const remaining = Math.max(0, budget - pinnedTotal);
	const freeWeight = dates.reduce(
		(sum, date) => sum + (isPinned(date) ? 0 : (weights[date] ?? 0)),
		0
	);

	return Object.fromEntries(
		dates.map((date) => [
			date,
			isPinned(date)
				? (pins[date] as number)
				: freeWeight > 0
					? (remaining * (weights[date] ?? 0)) / freeWeight
					: 0,
		])
	);
};

/**
 * The month graph's rule one level down, and the same check the backend
 * makes (see DayPins): pins must fit inside the month, and when every day is
 * pinned so nothing can absorb a difference, land on it exactly. A euro of
 * slack either way is rounding.
 */
export const daysFitMonth = (
	dates: string[],
	pins: DayMap<number | null>,
	budget: number
): boolean => {
	const pinned = dates.filter(
		(date) => pins[date] !== undefined && pins[date] !== null
	);
	const pinnedTotal = pinned.reduce((sum, date) => sum + (pins[date] as number), 0);

	if (pinnedTotal > budget + 1) return false;

	return pinned.length < dates.length || Math.abs(pinnedTotal - budget) <= 1;
};

/**
 * Replaces one month's entries in a sparse day map, leaving every other
 * month's alone — the calendar only ever edits the month it shows, but the
 * row it writes covers the whole year.
 */
export const replaceMonth = (
	all: DayMap<number> | undefined | null,
	dates: string[],
	month: DayMap<number>
): DayMap<number> => {
	const inMonth = new Set(dates);
	const kept = Object.entries(all ?? {}).filter(([date]) => !inMonth.has(date));

	return Object.fromEntries(
		[...kept, ...Object.entries(month)].sort(([a], [b]) => (a < b ? -1 : 1))
	);
};

/**
 * Euros each day of a month gets, straight from a day configuration: the
 * locked days' weights and the pins as stored, the auto days filled in from
 * impressions. What the Fill & CPM calendar prices each day against — the
 * same split the Monthly Budget Distribution calendar shows, and the same one
 * the resolver uses (see DayDistribution.php).
 */
export const dayBudgetsFor = (
	dates: string[],
	shares: DayMap<number>,
	lockedWeights: DayMap<number>,
	pins: DayMap<number>,
	budget: number
): DayMap<number> => {
	const locked = Object.fromEntries(
		Object.keys(lockedWeights).map((date) => [date, true])
	);

	return dayAmounts(
		dates,
		rebalanceDays(dates, { ...lockedWeights }, locked, pins, shares),
		pins,
		budget
	);
};
